import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { CalendarIcon, ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { formatTime, formatDateShort } from '../../utils/timeUtils';
import PlanningCrewFilter from './PlanningCrewFilter';

const DraggableSession = ({ session, index, isSelected, onSessionSelect }) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'session',
      item: { id: session.id, session, sourceIndex: index, sourceType: 'unscheduled' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [session, index]
  );

  // Check if this session has group information (session splitting)
  const hasGroupInfo = session.group_info && session.group_info.total_instances > 1;
  const isPartialSession = session.group_info && !session.is_original;

  const handleClick = (e) => {
    // Only handle click if not dragging
    if (!isDragging && onSessionSelect) {
      e.preventDefault();
      e.stopPropagation();
      onSessionSelect(session);
    }
  };

  return (
    <div
      ref={drag}
      onClick={handleClick}
      className={`min-w-64 flex-shrink-0 cursor-grab select-none rounded-lg border bg-white p-3 shadow-sm transition-all hover:shadow-md active:cursor-grabbing
                ${isDragging ? 'rotate-1 opacity-50 shadow-xl' : ''}
                ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200'}
            `}
    >
      <div className="mb-2 flex items-start justify-between">
        <div className="flex flex-1 items-center gap-2">
          <h3 className="truncate pr-2 text-sm font-medium text-gray-900">{session.title}</h3>
          {hasGroupInfo && (
            <div className="flex items-center gap-1">
              <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-800">
                Split
              </span>
              <span className="text-xs text-gray-500">
                {session.group_info.scheduled_instances}/{session.group_info.total_instances}
              </span>
            </div>
          )}
        </div>
        <span className="whitespace-nowrap rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
          {session.training_type || 'Training'}
        </span>
      </div>

      <div className="mb-2 flex items-center text-xs text-gray-600">
        <ClockIcon className="mr-1 h-3 w-3" />
        <span>
          {session.default_start_time && session.default_end_time
            ? `${formatTime(session.default_start_time)} - ${formatTime(session.default_end_time)}`
            : `${session.duration || 90} minutes`}
        </span>
      </div>

      <div className="mb-2 flex items-center text-xs text-gray-600">
        <UserGroupIcon className="mr-1 h-3 w-3" />
        <span>
          {session.crew_count} {session.crew_count === 1 ? 'crew' : 'crews'}
          {session.assigned_crews && session.assigned_crews.length > 0 && (
            <span className="ml-1 text-gray-500">
              ({session.assigned_crews.map((c) => c.name).join(', ')})
            </span>
          )}
        </span>
      </div>

      {hasGroupInfo && session.group_info.has_unscheduled_remainder && (
        <div className="mb-1 text-xs text-orange-600">
          Partially scheduled - {session.crew_count} crews remaining
        </div>
      )}

      {session.required_participants && (
        <div className="mb-1 flex items-center text-xs text-gray-600">
          <UserGroupIcon className="mr-1 h-3 w-3" />
          <span>{session.required_participants} participants required</span>
        </div>
      )}

      {session.description && (
        <p className="truncate text-xs text-gray-500" title={session.description}>
          {session.description}
        </p>
      )}
    </div>
  );
};

DraggableSession.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    duration: PropTypes.number,
    default_start_time: PropTypes.string,
    default_end_time: PropTypes.string,
    training_type: PropTypes.string,
    required_participants: PropTypes.number,
    date: PropTypes.string,
    assigned_crews: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSessionSelect: PropTypes.func,
};

const DailyUnscheduledSessions = ({
  sessions = [],
  allCrews = [],
  selectedCrews = [],
  handleCrewFilterChange,
  setSelectedCrews,
  selectedUnscheduledSession,
  onSessionSelect,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const SIDEBAR_WIDTH = 256; // Sidebar width in pixels (w-64 = 256px)
  const CONTAINER_PADDING = 32; // Padding buffer for container
  const MIN_SESSION_WIDTH = 256; // Minimum width for each session card (min-w-64 = 256px)

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate available width for the session container
  const availableWidth = windowWidth - SIDEBAR_WIDTH - CONTAINER_PADDING;
  const maxContainerWidth = Math.max(MIN_SESSION_WIDTH, availableWidth);

  if (!sessions || sessions.length === 0) {
    return (
      <div className="border-b border-gray-200 bg-white p-4">
        <div className="flex items-start gap-4">
          {/* Crew Filter - Left Side */}
          <div className="flex-shrink-0">
            <PlanningCrewFilter
              allCrews={allCrews}
              selectedCrews={selectedCrews}
              handleCrewFilterChange={handleCrewFilterChange}
              setSelectedCrews={setSelectedCrews}
            />
          </div>

          {/* Sessions Area - Right Side */}
          <div className="min-w-0 flex-1">
            <h2 className="mb-3 text-lg font-medium text-gray-900">Unscheduled Sessions (0)</h2>
            <div className="py-8 text-center text-gray-500">
              <p>No unscheduled sessions for the selected date and crew filter.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200 bg-white p-4">
      <div className="flex items-start gap-4">
        {/* Crew Filter - Left Side */}
        <div className="flex-shrink-0">
          <PlanningCrewFilter
            allCrews={allCrews}
            selectedCrews={selectedCrews}
            handleCrewFilterChange={handleCrewFilterChange}
            setSelectedCrews={setSelectedCrews}
          />
        </div>

        {/* Sessions Area - Right Side */}
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Unscheduled Sessions ({sessions.length})
            </h2>
          </div>

          <div
            className="flex gap-3 overflow-x-auto pb-2"
            style={{
              maxWidth: `${maxContainerWidth}px`,
            }}
          >
            {sessions.map((session, index) => (
              <DraggableSession
                key={session.id}
                session={session}
                index={index}
                isSelected={selectedUnscheduledSession?.id === session.id}
                onSessionSelect={onSessionSelect}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

DailyUnscheduledSessions.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      duration: PropTypes.number,
      default_start_time: PropTypes.string,
      default_end_time: PropTypes.string,
      training_type: PropTypes.string,
      required_participants: PropTypes.number,
      date: PropTypes.string,
      assigned_crews: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  allCrews: PropTypes.array,
  selectedCrews: PropTypes.array,
  handleCrewFilterChange: PropTypes.func,
  setSelectedCrews: PropTypes.func,
  selectedUnscheduledSession: PropTypes.object,
  onSessionSelect: PropTypes.func,
};

export default DailyUnscheduledSessions;
